import {LogLevel, LoggerConfig, LogContext, LogCode} from '@/logger'
import { selectStrategy } from '@/logger/config';
import { LoggingStrategy } from '@/logger/strategies';
import packageJson from '../../package.json';
import { createCorrelationId } from '@/lib/correlationId';

/**
 * Singleton logger class that provides a unified logging interface.
 * Supports multiple logging strategies (console, remote logging, etc)
 *
 * @example
 * ```typescript
 * import { logger } from '@/logger';
 * await logger.initialize(config);
 * logger.info(
 *   'User logged in',
 *   {
 *     logCode: LogCode.INTERNAL,
 *     component: 'stores/auth.ts',
 *     dataFields: { userId: '123' }
 *   }
 * );
 * logger.error(
 *   'Sign in failure,
 *   {
 *     logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK,
 *     component: 'stores/auth.ts',
 *     dataFields: { userId: '123' }
 *   },
 *   new Error('Something went wrong')
 * );
 * ```
 */
class Logger {
  private static instance: Logger | undefined;
  private strategy!: LoggingStrategy;
  private initialized = false;
  private readonly appVersion = packageJson.version;

  private constructor() {}

  /**
   * Returns the singleton instance of the Logger.
   *
   * @returns The Logger instance
   */
  public static getInstance(): Logger {
    // Lazily initializes Logger.instance with a new Logger if it's null or undefined, using nullish coalescing operator
    Logger.instance ??= new Logger();
    return Logger.instance;
  }

  /**
   * Initializes the logger with the provided configuration.
   * Must be called before using any logging methods.
   *
   * @param config - The logger configuration
   * @returns Promise that resolves when initialization is complete
   *
   * @example
   * ```typescript
   * const config = createLoggerConfig(import.meta.env.MODE);
   * await logger.initialize(config);
   * ```
   */
  public initialize(config: LoggerConfig): void {
    if (this.initialized) {
      return;
    }

    try {
      this.strategy = selectStrategy(config);
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize logger strategy. Falling back to console.', error);
      this.initialized = false;
      throw error;
    }
  }

  /**
   * Resets the singleton instance. Used for testing purposes only.
   * @internal
   */
  public static resetInstance(): void {
    Logger.instance = undefined;
  }

  // Facade methods

  /**
   * Logs a debug message. Use for detailed information only when diagnosing problems.
   *
   * @param message - The log message
   * @param context - Optional context object with additional data
   * @param error - Optional error object
   *
   * @example
   * ```typescript
   * const logContext = { sessionId: 'sess-abc-456', component: 'stores/auth.ts', route: '/dashboard/profile',logCode: LogCode.INTERNAL, dataFields: { userId: '123' } };
   *
   * logger.debug('Processing user data', logContext);
   * ```
   */
  public debug(message: string, context?: LogContext, error?: Error | unknown): void {
    this.log(LogLevel.DEBUG, message, context, error);
  }

  /**
   * Logs an informational message. Use for general application flow information.
   *
   * @param message - The log message
   * @param context - Optional context object with additional data
   * @param error - Optional error object
   *
   * @example
   * ```typescript
   * const logContext = { sessionId: 'sess-abc-456', component: 'stores/auth.ts', route: '/dashboard/profile',logCode: LogCode.INTERNAL, dataFields: { userId: '123' } };
   *
   * logger.info('User logged in successfully', logContext);
   * ```
   */
  public info(message: string, context?: LogContext, error?: Error | unknown): void {
    this.log(LogLevel.INFO, message, context, error);
  }

  /**
   * Logs a warning message. Use for unexpected behavior that doesn't prevent operation.
   *
   * @param message - The log message
   * @param context - Optional context object with additional data
   * @param error - Optional error object
   *
   * @example
   * ```typescript
   * const logContext = { sessionId: 'sess-abc-456', component: 'stores/auth.ts', route: '/dashboard/profile',logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK, dataFields: { userId: '123' } };
   *
   * logger.warn('API response took longer than expected', logContext);
   * ```
   */
  public warn(message: string, context?: LogContext, error?: Error | unknown): void {
    this.log(LogLevel.WARN, message, context, error);
  }

  /**
   * Logs an error message. Use when functionality is impaired but the application continues.
   *
   * @param message - The log message
   * @param context - Optional context object with additional data
   * @param error - Optional error object
   *
   * @example
   * ```typescript
   * const logContext = { sessionId: 'sess-abc-456', component: 'stores/auth.ts', route: '/dashboard/profile',logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK, dataFields: { userId: '123' } };
   *
   *  logger.error('Failed to save user preferences', logContext, error);
   * ```
   */
  public error(message: string, context?: LogContext, error?: Error | unknown): void {
    this.log(LogLevel.ERROR, message, context, error);
  }

  /**
   * Logs a critical message. Use for severe errors that may cause system failure or data loss.
   * These should trigger immediate alerts and require urgent attention.
   *
   * @param message - The log message
   * @param context - Optional context object with additional data
   * @param error - Optional error object
   *
   * @example
   * ```typescript
   * const logContext = { sessionId: 'sess-abc-456', component: 'stores/auth.ts', route: '/dashboard/profile',logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK, dataFields: { userId: '123' } };
   *
   *  logger.critical('Authentication service unavailable', logContext, error);
   * ```
   */
  public critical(message: string, context?: object, error?: Error | unknown): void {
    this.log(LogLevel.CRITICAL, message, context, error);
  }

  private serializeError(error: Error) {
    return {
      ...error,
      message: error.message,
      stacktrace: error.stack,
      type: error.name,
      // Include any custom properties
      ...Object.getOwnPropertyNames(error).reduce((acc, key) => {
        if (!['name', 'message', 'stack'].includes(key)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const propertyValue = (error as any)[key];
          const isTruthyArray = propertyValue instanceof Array && propertyValue.length > 0;
          const hasErrorItem = (pv: unknown[]) => pv.some((item: unknown) => item instanceof Error)

          if (key === 'errors' && isTruthyArray && hasErrorItem(propertyValue))
            // Serialize errors in nested `errors` array
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            acc[key] = (error as any)[key].map(
              (e: Error | unknown) =>
                e instanceof Error ? this.serializeError(e) : e
            )
          else
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            acc[key] = (error as any)[key];
        }
        return acc;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, {} as any)
    };
  }

  /**
   * Core logging method that routes messages to the configured logging strategy.
   * Falls back to console logging if the logger is not initialized.
   *
   * @private
   * @param level - The log level
   * @param message - The log message
   * @param context - Optional context object
   * @param error - Optional error object
   */
  private log(level: LogLevel, message: string, context?: LogContext, error?: Error | unknown): void {
    const enrichedContext = {
      // default LogCode if not set by caller
      logCode: LogCode.INTERNAL,
      correlationId: context?.correlationId ?? createCorrelationId(),
      ...context,
      appVersion: this.appVersion,
      boundedContext: 'eclips-content-ui',
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let serializedError: any = undefined;
    if (error) {
      if (error instanceof Error) {
        serializedError = this.serializeError(error);
      } else {
        serializedError = error;
      }
    }

    if (this.initialized && this.strategy) {
      this.strategy.log(level, message, enrichedContext, serializedError);
    } else {
      console.warn('Logger or strategy not initialized, falling back to console');
      if (level === LogLevel.CRITICAL) {
        // console.critical does not exist
        console.error(message, enrichedContext, serializedError);
        return;
      }
      console[level](message, enrichedContext, serializedError);
    }
  }
}

const logger = Logger.getInstance();

export { logger, Logger };
